import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blogPosts';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || '';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  const articleSchema = useMemo(() => {
    if (!post) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      image: {
        '@type': 'ImageObject',
        url: post.image,
        width: 800,
        height: 600,
      },
      author: {
        '@type': 'Person',
        name: post.author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Good Pitch Roofing',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/efcca1ae64e2adea8f6cbcf3fc677bc7/dcb07f32778ecb4afada9f63cf477c36.png',
        },
      },
      datePublished: post.date,
      dateModified: post.date,
      url: `${siteUrl}/blog/${post.slug}`,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/blog/${post.slug}`,
      },
      articleSection: post.category,
      keywords: `${post.category}, Oklahoma roofing, roof repair, insurance claim roofing`,
    };
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${post.title} | Good Pitch Roofing`}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        keywords={`${post.category}, Oklahoma roofing, ${post.title.toLowerCase()}, Good Pitch Roofing`}
        ogTitle={post.title}
        ogDescription={post.excerpt}
        ogImage={post.image}
        ogType="article"
        schema={articleSchema}
      />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
                <i className="ri-home-smile-line text-2xl text-white"></i>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">Good Pitch Roofing</div>
                <div className="text-xs text-gray-600">License #80007301</div>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Services</Link>
              <Link to="/#process" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Process</Link>
              <Link to="/#testimonials" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Testimonials</Link>
              <Link to="/blog" className="text-sm font-medium text-orange-500 transition-colors whitespace-nowrap">Blog</Link>
              <Link to="/#contact" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Contact</Link>
              <a href="tel:405-397-2783" className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>405-397-2783
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Image */}
      <div className="relative pt-24 w-full h-96 overflow-hidden">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium mb-4 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
            <div className="inline-block px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full mb-4 whitespace-nowrap">
              {post.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{post.title}</h1>
            <div className="flex items-center gap-4 text-white/90 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                <span>{post.author}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>{post.date}</span>
              </div>
              <span>•</span>
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="prose prose-lg max-w-none">
              <div className="text-xl text-gray-700 leading-relaxed mb-8 font-medium">
                {post.excerpt}
              </div>
              
              <div 
                className="text-gray-700 leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Share this article:</div>
                <div className="flex items-center gap-3">
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-orange-500 text-gray-700 hover:text-white rounded-lg transition-all">
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-orange-500 text-gray-700 hover:text-white rounded-lg transition-all">
                    <i className="ri-twitter-x-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-orange-500 text-gray-700 hover:text-white rounded-lg transition-all">
                    <i className="ri-linkedin-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-orange-500 text-gray-700 hover:text-white rounded-lg transition-all">
                    <i className="ri-mail-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Back to Blog */}
            <div className="mt-12">
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                View All Articles
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              {/* CTA Card */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-8 text-white shadow-xl">
                <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-phone-line text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">Need Roofing Help?</h3>
                <p className="text-white/90 mb-6 text-sm leading-relaxed">
                  Get a free inspection and quote from Oklahoma's trusted roofing experts. We work directly with insurance!
                </p>
                <a 
                  href="tel:405-397-2783"
                  className="block w-full px-6 py-3 bg-white text-orange-500 text-center font-bold rounded-lg hover:bg-gray-50 transition-all shadow-lg mb-3 whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>405-397-2783
                </a>
                <Link
                  to="/#contact"
                  className="block w-full px-6 py-3 bg-white/10 backdrop-blur-sm text-white text-center font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap"
                >
                  Request Free Inspection
                </Link>
              </div>

              {/* Recent Articles */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Articles</h3>
                <div className="space-y-4">
                  {blogPosts.filter(p => p.slug !== slug).slice(0, 3).map((recentPost) => (
                    <Link 
                      key={recentPost.slug}
                      to={`/blog/${recentPost.slug}`}
                      className="block group"
                    >
                      <div className="flex gap-3">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <img 
                            src={recentPost.image} 
                            alt={recentPost.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-orange-500 transition-colors line-clamp-2 mb-1">
                            {recentPost.title}
                          </h4>
                          <div className="text-xs text-gray-500">{recentPost.date}</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}